import React from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { AppContent, AppPage, ErrorComponents, withApollo, useFeatureToggle } from '@css-xp/base';
//@ts-ignore
import CatalogLogo from '@css-xp/base/assets/img/catalog-logo.png';
//@ts-ignore
import AtlassianBookIllustration from '@css-xp/base/assets/img/atlassian-book-illustration.svg';
// @ts-ignore
import CatalogIllustration from '@css-xp/base/assets/img/catalog-illustration.svg';
import { useApplicationContext } from '@css-xp/base/common-context';
import {
  CATALOGUE_PAGE_SIDEBARS,
  initialBillingState,
  initialFilterState,
  initialIsTableLoading,
  initialQuerySearch,
  initialSearchResults,
  PartnerCatalogueProvider,
  PartnerCatalogueProviderProviderConfig,
  PartnerQuoteCatalogue,
  ProductsHeader,
  usePartnerCatalogueContext,
} from '@css-xp/ppc-checkout';
import { ProductsTableOrderFlow } from './products-table';
import { PPC_PAGE_TITLE, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import PartnerProductCatalogueContext from '@css-xp/ppc-checkout/src/products-in-catalogue/contexts/product-catalogue-context';
import { useTitle, useOpenClose } from '@css-xp/ppc-custom-hooks';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import {
  DiscountTierType,
  PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING,
} from '@css-xp/ppc-catalogue-page/src/constants/partner-promotions';
import { ProductPricingPane } from '@css-xp/ppc-catalogue-page/src/sidepane';
import { ParentEntitlementDetails } from '@css-xp/ppc-checkout/src/types';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { ProxyGraphClients } from '@atlassian/temp-client-utils';
import { FloatingOrderSummary } from './floating-order-summary';
import './catalogue-search-page.scss';
import { CatalogueContextItems } from '../types';
import { NativeProductResponse } from '../../../../types';

interface CataloguePageContentProps {
  catalogMessage: JSX.Element;
  allowedParentProductIdsForAppsOrder?: string[] | undefined;
  isJWMDisabled?: boolean;
  isAtlasDisabled?: boolean;
  isLoomDisabled?: boolean;
  isSandboxCloudsite?: boolean;
  isMultiCurrencyEnabled?: boolean;
  showProductsInModal?: boolean;
  checkedProductsList?: PartnerProductCatalogueItem[];
  setCheckedProductsList?: (_checkedProductsList: PartnerProductCatalogueItem[]) => void;
  isAppificationEnabled?: boolean;
}

interface CataloguePageProps {
  onAddToCart?: (_OrderItems: CatalogueContextItems) => void;
  existingCloudSiteProductIds?: string[];
  loading?: boolean;
  error?: Error;
  nativeProduct: NativeProductResponse;
  allowedAppsParentEntitlements?: string[];
  cloudSitesEntitlements?: Record<string, ParentEntitlementDetails>;
  isSandboxCloudsite?: boolean;
  existingSandboxCloudSiteProductIds?: string[];
  showProductsInModal?: boolean;
  checkedProductsList?: PartnerProductCatalogueItem[];
  setCheckedProductsList?: (_checkedProductsList: PartnerProductCatalogueItem[]) => void;
}

//Override the styles for the product table, to be seen in the modal
const ProductTableWrapper = styled.div`
  > div {
    max-width: 100%;
  }
  .products-table .products-list-table {
    margin-top: 0;
    thead > tr > th {
      padding: 0 12px !important;
    }
    tbody {
      border-bottom: none;
      > tr {
        &:hover {
          background: ${token('elevation.surface.hovered')} !important;
        }
      }
    }
    colgroup {
      .col-10 {
        width: 6%;
      }
      .col-60 {
        width: 74%;
      }
      .col-30 {
        width: 20%;
      }
    }
  }
`;

const allowedSidebars = {
  [CATALOGUE_PAGE_SIDEBARS.PRODUCT_PRICING_PLAN]: ProductPricingPane,
};

const getPromotionId = (discountTier: DiscountTierType): string | null => {
  const applicableDiscountTier = Object.keys(PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING).find((tier) =>
    discountTier.includes(tier)
  );
  return applicableDiscountTier
    ? PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING[applicableDiscountTier as DiscountTierType]
    : null;
};

const CataloguePageContent = ({
  catalogMessage,
  allowedParentProductIdsForAppsOrder,
  isJWMDisabled,
  isAtlasDisabled,
  isLoomDisabled,
  isSandboxCloudsite,
  isMultiCurrencyEnabled,
  showProductsInModal = false,
  checkedProductsList,
  setCheckedProductsList,
  isAppificationEnabled,
}: CataloguePageContentProps) => {
  const { isPartnerCatalogueInformationLoading, quoteItems } = usePartnerCatalogueContext();

  if (showProductsInModal) {
    return (
      <ProductTableWrapper>
        <ProductsTableOrderFlow
          allowedParentProductIdsForAppsOrder={allowedParentProductIdsForAppsOrder}
          isJWMDisabled={isJWMDisabled}
          isAtlasDisabled={isAtlasDisabled}
          isLoomDisabled={isLoomDisabled}
          isSandboxCloudsite={isSandboxCloudsite}
          isMultiCurrencyEnabled={isMultiCurrencyEnabled}
          showCheckbox={true}
          checkedProductsList={checkedProductsList}
          setCheckedProductsList={setCheckedProductsList}
          isAppificationEnabled={isAppificationEnabled}
        />
      </ProductTableWrapper>
    );
  }

  return (
    <>
      <div>
        {isPartnerCatalogueInformationLoading && (
          <div className="spinner-container">
            <Spinner testId="loading-product-information" size={'large'} />
          </div>
        )}
      </div>
      <div className={classNames({ 'blur-background': isPartnerCatalogueInformationLoading })}>
        <ProductsHeader
          filterOptions={[
            {
              key: 'includeProperties',
              values: ['vendorName'],
            },
          ]}
          hideCatalogExcludedProducts={true}
        />
        <ProductsTableOrderFlow
          allowedParentProductIdsForAppsOrder={allowedParentProductIdsForAppsOrder}
          isJWMDisabled={isJWMDisabled}
          isAtlasDisabled={isAtlasDisabled}
          isLoomDisabled={isLoomDisabled}
          isSandboxCloudsite={isSandboxCloudsite}
          isMultiCurrencyEnabled={isMultiCurrencyEnabled}
          isAppificationEnabled={isAppificationEnabled}
        />
        {!(quoteItems.length > 0) && catalogMessage}
        <PartnerQuoteCatalogue />
      </div>
    </>
  );
};

export const CatalogueSearchPage = ({
  nativeProduct,
  loading,
  error,
  onAddToCart,
  existingCloudSiteProductIds,
  allowedAppsParentEntitlements,
  cloudSitesEntitlements,
  isSandboxCloudsite,
  existingSandboxCloudSiteProductIds,
  showProductsInModal = false,
  checkedProductsList,
  setCheckedProductsList,
}: CataloguePageProps) => {
  const { data } = nativeProduct;
  const [billingFrequencyState, setCurrentBillingState] = React.useState(initialBillingState);
  const [filterState, setFilter] = React.useState(initialFilterState);
  const [isTableLoading, setIsTableLoading] = React.useState(initialIsTableLoading);
  const [searchResults, setSearchResults] = React.useState(initialSearchResults);
  const [querySearch, setQuerySearch] = React.useState(initialQuerySearch);
  const { isOpen, open, close } = useOpenClose();
  useTitle(PPC_PAGE_TITLE.CATALOG);
  const context = useApplicationContext();

  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();

  const partnerAccount = context?.partnerAccounts?.find(
    (account: { transactionAccountId: string; discountTier: string }) => account.transactionAccountId === txaId
  );

  const discountTier = partnerAccount?.discountTier ?? '';
  const applicablePromotionId = getPromotionId(discountTier);

  const renderError = () => {
    if (error) {
      const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
      return <GenericError error={error} />;
    }
  };

  const productCatalogueValue = {
    billingFrequencyState,
    setCurrentBillingState,
    existingCloudSiteProductIds,
    filterState,
    setFilter,
    isTableLoading,
    setIsTableLoading,
    querySearch,
    setQuerySearch,
    searchResults,
    setSearchResults,
    loading,
    data,
  };

  const catalogueQuoteConfig: PartnerCatalogueProviderProviderConfig = {
    isCreateQuoteEnabled: true,
    pricingPlanCycle: PricingPlanCycle.MONTHLY,
    allowedAppsParentEntitlements,
    cloudSitesEntitlements,
    sandboxCloudSiteParentProductIds: existingSandboxCloudSiteProductIds,
  };

  // where we mapped the entitlements with jira container relationship product id or product id
  // We are getting only parent product ids for apps order
  const allowedParentProductIdsForAppsOrder = Object.keys(cloudSitesEntitlements ?? {});

  const catalogMessage = (
    <div className="catalogue-message">
      <img src={CatalogLogo} className="catalogue-logo" />
      <div className="catalogue-heading">
        <div className="atlassian-book-container">
          {/* @ts-ignore */}
          <img src={AtlassianBookIllustration} />
        </div>
        <h1 className="catalogue-title">Welcome to Atlassian Catalog👋</h1>
        <div className="description-container">
          <p className="description">Start selecting products and apps to create a quote or an order.</p>
          <br />
          <p className="description">
            Alternatively, this is the place you’ll find all the latest pricing plan, changes history and more.
          </p>
        </div>
      </div>
    </div>
  );

  const catalogMessageOrderFlow = (
    <div className="catalogue-message">
      <img src={CatalogLogo} className="catalogue-logo" />
      <div className="catalogue-heading">
        <div className="atlassian-book-container">
          {/* @ts-ignore */}
          <img src={CatalogIllustration} />
        </div>
        <h1 className="catalogue-title">Add new subscriptions</h1>
      </div>
    </div>
  );

  const pageContent = (
    <CataloguePageContent
      catalogMessage={catalogMessageOrderFlow}
      allowedParentProductIdsForAppsOrder={allowedParentProductIdsForAppsOrder ?? []}
      isJWMDisabled
      isAtlasDisabled
      isLoomDisabled={!isEnabled(STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF)}
      isSandboxCloudsite={isSandboxCloudsite}
      isMultiCurrencyEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES)}
      showProductsInModal={showProductsInModal}
      checkedProductsList={checkedProductsList}
      setCheckedProductsList={setCheckedProductsList}
      isAppificationEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION)}
    />
  );

  if (showProductsInModal) {
    return (
      <PartnerProductCatalogueContext.Provider value={productCatalogueValue}>
        <PartnerCatalogueProvider config={catalogueQuoteConfig}>
          <AppContent className="app-content-products">{pageContent}</AppContent>
        </PartnerCatalogueProvider>
      </PartnerProductCatalogueContext.Provider>
    );
  }

  return (
    <PartnerProductCatalogueContext.Provider value={productCatalogueValue}>
      <PartnerCatalogueProvider config={catalogueQuoteConfig}>
        <AppPage className="catalogue-page-overrides cart-catalogue-page" allowedSidebars={allowedSidebars}>
          <ScreenEventAnalytics
            event={{
              name: AnalyticsEventSource.PARTNER_CATALOG_SCREEN,
              attributes: {},
            }}
          />
          <AppContent className={!loading ? 'app-content-products' : ''}>
            {loading ? <LoadingIndicator /> : renderError() ?? pageContent}
          </AppContent>
          <FloatingOrderSummary
            transactionAccountId={txaId}
            applicablePromotionId={applicablePromotionId ?? ''}
            onCheckout={(orderItems: CatalogueContextItems) => {
              onAddToCart?.(orderItems);
              open();
            }}
            isJPDAsJiraFamilyEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY)}
          />
        </AppPage>
      </PartnerCatalogueProvider>
    </PartnerProductCatalogueContext.Provider>
  );
};

export default withApollo(CatalogueSearchPage, ProxyGraphClients['SXP']);
